import { Check, LoaderCircle, Minus, Plus } from 'lucide-react'
import type { RevisionPreviewData } from '@/frontend/pages/Dashboard/BusinessProcesses/revisionPreview'
import { useRevisionPreview } from '@/frontend/pages/Dashboard/BusinessProcesses/useRevisionPreview'

// Plain text avoids interpreting model-generated HTML, links or code in an
// unvalidated preview. Stable rows preserve reading position across deltas.
export default function RevisionPreviewPanel({ preview }: { preview: RevisionPreviewData }) {
  const { edits, disconnected } = useRevisionPreview(preview)
  const { copy } = preview
  const applied = preview.state === 'applied'
  const awaitingSavedResponse = preview.state === 'streaming' && !preview.live_available
  return (
    <section className="space-y-3 rounded-lg border bg-background p-4" aria-label={copy.title}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">{copy.title}</h3>
          <p className="text-xs text-muted-foreground">{copy[preview.field]} · {copy.base} {preview.base_version_number}</p>
        </div>
        <p role="status" className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {applied ? <Check className="size-3.5" aria-hidden /> : preview.state === 'streaming' && !disconnected
            ? <LoaderCircle className="size-3.5 animate-spin motion-reduce:animate-none" aria-hidden /> : null}
          {disconnected ? copy.disconnected : awaitingSavedResponse ? copy.awaiting_saved : copy[preview.state]}
        </p>
      </div>
      {edits.length === 0 ? (
        <p className="text-sm text-muted-foreground">{awaitingSavedResponse ? copy.creator_stream : preview.state === 'streaming' ? copy.waiting : copy.empty}</p>
      ) : (
        <ol className="max-h-[32rem] space-y-3 overflow-y-auto">
          {edits.map((edit, index) => (
            <li key={index} className="overflow-hidden rounded-md border text-sm">
              <div className="grid divide-y md:grid-cols-2 md:divide-x md:divide-y-0">
                <div className="min-w-0 bg-destructive/5 p-3">
                  <p className="mb-2 flex items-center gap-1 text-xs font-medium text-muted-foreground"><Minus className="size-3" aria-hidden />{copy.before}</p>
                  <pre className="whitespace-pre-wrap break-words font-sans leading-relaxed"><del className="no-underline">{edit.before}</del></pre>
                </div>
                <div className="min-w-0 bg-agent-soft p-3">
                  <p className="mb-2 flex items-center gap-1 text-xs font-medium text-muted-foreground"><Plus className="size-3" aria-hidden />{copy.after}</p>
                  <pre className="whitespace-pre-wrap break-words font-sans leading-relaxed"><ins className="no-underline">{edit.after || copy.removed}</ins></pre>
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
      {edits.length >= 40 && <p className="text-xs text-muted-foreground">{copy.limit}</p>}
    </section>
  )
}
